import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import type { DP } from '../../../drupal/blockDefinitions'
import { dummyData, randomTitle, randomWords } from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import type { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { highlightBlockClasses, HighlightBlockLayout } from './highlightBlockWithImage.layout'
import type { HighlightBlockWithImageProps } from './highlightBlockWithImage.state'
import { parseDrupalImageUrls } from '../../../drupal/utils/modernImage'
import { highlightImageSrcSet } from '../../../drupal/utils/imageSizes'

export const highlightBlockWithImage: ModernBlock<HighlightBlockWithImageProps, any, DP.HighlightWithImage> = {
  kind: 'highlightWithImageBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    fromJSX((updateBlock) => (
      <HighlightBlockLayout.Inner id={`highlightblock-image-${blockIndex}`} amountNested={amountNested!}>
        <HighlightBlockLayout.Content
          label={blockProps.label}
          title={blockProps.title}
          intro={blockProps.intro}
          text={blockProps.text}
          showPrimary={blockProps.showPrimary}
          showSecondary={blockProps.showSecondary}
          primaryAction={blockProps.primaryAction!}
          secondaryAction={blockProps.secondaryAction!}
          showActionAsLink={blockProps.showActionAsLink}
          image={blockProps.image}
          bgColour={blockProps.bgColour}
        />
      </HighlightBlockLayout.Inner>
    )),
  adapters: {
    drupal: (dpBlock: DP.HighlightWithImage, blockIndex: number) => {
      const target = dpBlock.target.type[0].target_id
      if (target != 'highlight_with_image') return undefined

      const fieldMediaImage = dpBlock.target.field_media[0]?.target.field_media_image[0]
      const fieldMediaSource = dpBlock.target.field_media[0]?.target.field_source[0]
        ? dpBlock.target.field_media[0]?.target.field_source
        : undefined
      const dpImages = fieldMediaImage
        ? parseDrupalImageUrls(fieldMediaImage, fieldMediaSource, highlightImageSrcSet)?.image
        : undefined
      return {
        title: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : undefined,
        text: dpBlock.target.field_body[0]?.processed,
        label: dpBlock.target.field_label ? dpBlock.target.field_label[0]?.value : undefined,
        intro: dpBlock.target.field_introduction ? dpBlock.target.field_introduction[0]?.value : undefined,
        bgColour: dpBlock.target.field_highlight_background_color ? dpBlock.target.field_highlight_background_color[0]?.value == 'darkred' ? 'darkred' : dpBlock.target.field_highlight_background_color[0]?.value == 'red' ? 'red' : 'lightred' : 'lightred',
        showPrimary: dpBlock.target.field_links[0] ? true : false,
        showSecondary: dpBlock.target.field_links[1] ? true : false,
        primaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.options?.attributes?.id! : '',
        }),
        secondaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.options?.attributes?.id! : '',
        }),
        anchorTitle: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : '',
        showActionAsLink: dpBlock.target.field_link_type[0]?.value == true ? true : false,
        inviewState: false,
        image:dpImages
      }
    }
  },
  sampleExample: [
    {
      title: randomTitle(),
      label: randomWords(),
      intro: dummyData.intro,
      text: dummyData.wysiwyg,
      bgColour: 'red',
      anchorTitle: 'highlight-image-sample-1',
      showPrimary: true,
      showSecondary: true,
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
      image:dummyData.image,
    },
    {
      title: randomTitle(),
      text: dummyData.wysiwyg,
      bgColour: 'red',
      anchorTitle: 'highlight-image-sample-2',
      showPrimary: false,
      showSecondary: false,
      showActionAsLink: false,
      image:dummyData.image,
    }
  ],
  seoEvents: (props, blockIndex) =>
    [
      props.showPrimary && props.primaryAction
        ? {
            id: `primary_button`,
            event: 'onClick',
            category: 'text_block',
            label: window.location.pathname,
            action: 'primary_' + props.primaryAction.title
          }
        : undefined,
      props.showSecondary && props.secondaryAction
        ? {
            id: `secondary_button`,
            event: 'onClick',
            category: 'text_block',
            label: window.location.pathname,
            action: 'secondary_' + props.secondaryAction.title
          }
        : undefined
    ].filter((i) => i != undefined) as SeoEvent[],
  inView: {
    showAs: 'section',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(highlightBlockClasses, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState
      }
    }
  },
  blockClassName: (props) => `
    ${getClassNameModern(highlightBlockClasses, 'block', 'mb-6')}
    ${props.bgColour ? HighlightBlockLayout.ColorSchemeClassName(props.bgColour) : 'bg-light'}
  `,
}
